import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Video, X } from 'lucide-react';
import cn from 'classnames';
import styles from './CollabIntroVideo.module.sass';
import { getAuthToken } from '../../utils/authHelpers';
import { COLLAB_INTRO_VIDEO_URL } from '../../constants/collabIntroVideo';

/**
 * "Learn More About Collabs" button + intro video modal.
 * Uses the same notification setting as the collab workspace (`show_collab_intro_video`).
 */
const CollabIntroVideo = ({ buttonClassName, wrapperClassName }) => {
  const [showIntroWidget, setShowIntroWidget] = useState(true);
  const [introExpanded, setIntroExpanded] = useState(false);
  const [introDismissedOnce, setIntroDismissedOnce] = useState(false);
  const [introDismissedForever, setIntroDismissedForever] = useState(false);
  const [introHideMenu, setIntroHideMenu] = useState(false);
  const [introConfirmForever, setIntroConfirmForever] = useState(false);
  const introVideoRef = useRef(null);

  const apiBase =
    typeof window !== 'undefined' && window.location.hostname === 'localhost'
      ? 'http://localhost:4000'
      : '';

  useEffect(() => {
    if (!COLLAB_INTRO_VIDEO_URL) return;
    const token = getAuthToken();
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/api/notifications/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data?.ok && data.settings?.show_collab_intro_video === false) {
          setShowIntroWidget(false);
        }
      } catch (_) {
        // Non-critical; default to showing on error
      }
    })();
  }, [apiBase]);

  const handleExpandIntro = useCallback(() => {
    setIntroExpanded(true);
    setIntroHideMenu(false);
    setIntroConfirmForever(false);
  }, []);

  const handleCollapseIntro = useCallback(() => {
    if (introVideoRef.current) {
      introVideoRef.current.pause();
    }
    setIntroExpanded(false);
    setIntroHideMenu(false);
    setIntroConfirmForever(false);
  }, []);

  const handleDismissOnce = useCallback(() => {
    setIntroDismissedOnce(true);
    setIntroExpanded(false);
    setIntroHideMenu(false);
    setIntroConfirmForever(false);
    if (introVideoRef.current) {
      introVideoRef.current.pause();
      introVideoRef.current.muted = true;
    }
  }, []);

  const handleDismissForever = useCallback(async () => {
    setIntroDismissedForever(true);
    setIntroExpanded(false);
    setIntroHideMenu(false);
    setIntroConfirmForever(false);
    if (introVideoRef.current) {
      introVideoRef.current.pause();
      introVideoRef.current.muted = true;
    }
    try {
      const token = getAuthToken();
      if (!token) return;
      await fetch(`${apiBase}/api/notifications/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ show_collab_intro_video: false }),
      });
    } catch (_) {}
  }, [apiBase]);

  const handleToggleHideMenu = useCallback(() => {
    setIntroHideMenu((prev) => !prev);
    setIntroConfirmForever(false);
  }, []);

  const handleHideForeverClick = useCallback(() => {
    setIntroConfirmForever(true);
  }, []);

  const introVisible =
    COLLAB_INTRO_VIDEO_URL && showIntroWidget && !introDismissedOnce && !introDismissedForever;

  if (!COLLAB_INTRO_VIDEO_URL) return null;

  return (
    <>
      {introVisible && (
        <span className={cn(wrapperClassName)}>
          <button
            type="button"
            className={cn(styles.learnMoreBtn, buttonClassName)}
            onClick={handleExpandIntro}
          >
            <Video size={14} />
            How to use My Collab Tracker
          </button>
        </span>
      )}

      {introVisible &&
        introExpanded &&
        createPortal(
          <div className={styles.introVideoOverlay} onClick={handleCollapseIntro}>
            <div
              className={styles.introVideoExpanded}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Collab introduction video"
            >
              <button
                type="button"
                className={styles.introVideoCloseBtn}
                onClick={handleCollapseIntro}
                aria-label="Close expanded video"
              >
                <X size={20} />
              </button>
              <video
                ref={introVideoRef}
                className={styles.introVideoExpandedPlayer}
                src={COLLAB_INTRO_VIDEO_URL}
                playsInline
                controls
              />
              <div className={styles.introVideoExpandedActions}>
                <div className={styles.introHideWrapper}>
                  <button type="button" className={styles.introHideBtn} onClick={handleToggleHideMenu}>
                    Hide
                  </button>
                  {introHideMenu && (
                    <div className={cn(styles.introHideMenu, styles.introHideMenuExpanded)}>
                      {!introConfirmForever ? (
                        <>
                          <button
                            type="button"
                            className={styles.introHideMenuOption}
                            onClick={handleDismissOnce}
                          >
                            Hide Once
                          </button>
                          <button
                            type="button"
                            className={styles.introHideMenuOption}
                            onClick={handleHideForeverClick}
                          >
                            Hide Forever
                          </button>
                        </>
                      ) : (
                        <div className={styles.introHideConfirm}>
                          <span className={styles.introHideConfirmText}>Are you sure?</span>
                          <div className={styles.introHideConfirmActions}>
                            <button
                              type="button"
                              className={styles.introHideConfirmYes}
                              onClick={handleDismissForever}
                            >
                              Yes
                            </button>
                            <button
                              type="button"
                              className={styles.introHideConfirmNo}
                              onClick={() => setIntroConfirmForever(false)}
                            >
                              No
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default CollabIntroVideo;
